import PropTypes from 'prop-types';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';

/**
 * A reusable collapsible section component with customizable header and content
 * Designed to replace Accordion for better styling control and simpler interaction
 */
const CollapsibleSection = ({
	title,
	icon,
	color,
	itemCount,
	isExpanded,
	onToggle,
	onOpenCreate,
	children,
}) => {
	// Handle toggle section without triggering other click events
	const handleToggle = (e) => {
		e.stopPropagation();
		onToggle();
	};

	// Handle create button click without triggering section toggle
	const handleOpenCreate = (e) => {
		e.stopPropagation();
		onOpenCreate();
	};

	return (
		<Paper
			sx={{
				mb: 2,
				overflow: 'hidden',
				borderRadius: 1,
				boxShadow: 'none',
				border: '1px solid',
				borderColor: 'divider',
				transition: 'all 0.2s ease',
			}}
		>
			{/* Section Header */}
			<Box
				onClick={handleToggle}
				sx={{
					display: 'flex',
					alignItems: 'center',
					p: 2,
					borderLeft: `4px solid ${color}`,
					bgcolor: 'background.paper',
					cursor: 'pointer',
					'&:hover': {
						bgcolor: 'rgba(0, 0, 0, 0.02)',
					},
				}}
			>
				{/* Left side - Icon and Title */}
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Box sx={{ color, mr: 1.5, display: 'flex', alignItems: 'center' }}>
						{icon}
					</Box>
					<Typography
						variant='h6'
						sx={{ fontWeight: 700, fontSize: 16, color }}
					>
						{title} {itemCount > 0 && `(${itemCount})`}
					</Typography>
				</Box>

				{/* left side Expand/Collapse icon */}
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
				</Box>
				<Box sx={{ ml: 'auto' }}>
					{onOpenCreate && (
						<IconButton
							size='small'
							onClick={handleOpenCreate}
							sx={{ mr: 1 }}
							aria-label='add'
						>
							<AddIcon />
						</IconButton>
					)}
				</Box>
			</Box>

			{/* Section Content */}
			{isExpanded && (
				<Box sx={{ p: 2, bgcolor: 'background.paper' }}>{children}</Box>
			)}
		</Paper>
	);
};

CollapsibleSection.propTypes = {
	title: PropTypes.string.isRequired,
	icon: PropTypes.node.isRequired,
	color: PropTypes.string.isRequired,
	itemCount: PropTypes.number,
	isExpanded: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	onOpenCreate: PropTypes.func,
	children: PropTypes.node,
};

CollapsibleSection.defaultProps = {
	itemCount: 0,
	onOpenCreate: null,
	children: null,
};

export default CollapsibleSection;
